import type { OrganisationEntitlementsDto, ProjectDto } from '@flowqa/shared';

type ProjectPlanOption = Pick<
  ProjectDto,
  'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'createdAt'
>;

export type PlanAlertTone = 'orange' | 'red';

export type PlanAlert = {
  key: string;
  tone: PlanAlertTone;
  message: string;
};

export type PlanBadge = {
  label: string;
  tone: 'orange' | 'red';
};

export type ProjectPlanOptionState = ProjectPlanOption & {
  locked: boolean;
  lockReason: string | null;
};

export type ResolvedPlanStatus = {
  alerts: PlanAlert[];
  badge: PlanBadge | null;
  projectOptions: ProjectPlanOptionState[];
  currentProjectLocked: boolean;
  uploadBlockedReason: string | null;
};

type ResolvePlanStatusInput = {
  entitlements: OrganisationEntitlementsDto | null;
  projects: ProjectPlanOption[];
  selectedProjectId: string | null;
  now?: Date;
};

const projectLockReason = 'Uploads are locked for this project on the current plan.';

const buildUsageAlertMessage = (executionCount: number, executionLimit: number): string => {
  const usagePercent = Math.round((executionCount / executionLimit) * 100);
  return `${executionCount} / ${executionLimit} (${usagePercent}%) run uploads used. Further run uploads will be blocked when limit is reached.`;
};

const sortProjectsByCreatedAt = (projects: ProjectPlanOption[]): ProjectPlanOption[] =>
  [...projects].sort((left, right) => {
    const leftTime =
      left.createdAt instanceof Date ? left.createdAt.getTime() : Number.MAX_SAFE_INTEGER;
    const rightTime =
      right.createdAt instanceof Date ? right.createdAt.getTime() : Number.MAX_SAFE_INTEGER;
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }
    return left.id.localeCompare(right.id);
  });

const resolveUsageAlert = (
  entitlements: OrganisationEntitlementsDto,
): { alert: PlanAlert | null; badge: PlanBadge | null } => {
  const executionLimit = entitlements.usage.executionLimit;
  if (executionLimit === null || executionLimit <= 0) {
    return { alert: null, badge: null };
  }

  const usageRatio = entitlements.usage.executionCount / executionLimit;
  if (usageRatio >= 0.9) {
    return {
      alert: {
        key: `usage-90-${entitlements.usage.periodEnd.toISOString()}`,
        tone: 'red',
        message: buildUsageAlertMessage(entitlements.usage.executionCount, executionLimit),
      },
      badge: { label: '90% Used', tone: 'red' },
    };
  }
  if (usageRatio >= 0.8) {
    return {
      alert: {
        key: `usage-80-${entitlements.usage.periodEnd.toISOString()}`,
        tone: 'orange',
        message: buildUsageAlertMessage(entitlements.usage.executionCount, executionLimit),
      },
      badge: { label: '80% Used', tone: 'orange' },
    };
  }

  return { alert: null, badge: null };
};

const resolveTrialAlert = (
  entitlements: OrganisationEntitlementsDto,
  now: Date,
): { alert: PlanAlert | null; badge: PlanBadge | null } => {
  if (entitlements.plan.code !== 'trial' || entitlements.trialEndsAt === null) {
    return { alert: null, badge: null };
  }

  const trialEndsAt = entitlements.trialEndsAt;
  const dayMs = 24 * 60 * 60 * 1000;
  const elapsedDays = Math.floor(
    (now.getTime() - entitlements.usage.periodStart.getTime()) / dayMs,
  );
  const remainingDays = Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / dayMs));

  if (elapsedDays >= 10) {
    return {
      alert: {
        key: `trial-10-${trialEndsAt.toISOString()}`,
        tone: 'red',
        message: `Your trial ends in ${remainingDays} day${remainingDays === 1 ? '' : 's'}. Upgrade soon to keep uploading new runs.`,
      },
      badge: { label: `${remainingDays}d Left`, tone: 'red' },
    };
  }
  if (elapsedDays >= 7) {
    return {
      alert: {
        key: `trial-7-${trialEndsAt.toISOString()}`,
        tone: 'orange',
        message: `Your trial has reached the one-week mark. ${remainingDays} day${remainingDays === 1 ? '' : 's'} remain before uploads stop.`,
      },
      badge: { label: `${remainingDays}d Left`, tone: 'orange' },
    };
  }

  return { alert: null, badge: null };
};

const resolveMemberAccessAlert = (
  entitlements: OrganisationEntitlementsDto,
  now: Date,
): { alert: PlanAlert | null; badge: PlanBadge | null } => {
  if (entitlements.plan.code !== 'solo' || entitlements.access.isPrimaryMember) {
    return { alert: null, badge: null };
  }

  const graceEndsAt = entitlements.access.memberGraceEndsAt;
  if (entitlements.access.currentUserWriteAccess && graceEndsAt instanceof Date) {
    const remainingDays = Math.max(
      0,
      Math.ceil((graceEndsAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)),
    );
    return {
      alert: {
        key: `member-grace-${graceEndsAt.toISOString()}`,
        tone: 'orange',
        message: `Solo keeps write access for the earliest member only. Your write access ends in ${remainingDays} day${remainingDays === 1 ? '' : 's'}.`,
      },
      badge: { label: 'Grace', tone: 'orange' },
    };
  }

  if (!entitlements.access.currentUserWriteAccess) {
    return {
      alert: {
        key: 'member-locked',
        tone: 'red',
        message:
          'Solo only allows the earliest member to manage uploads and settings. You still have read access.',
      },
      badge: { label: 'Locked', tone: 'red' },
    };
  }

  return { alert: null, badge: null };
};

const resolveProjectOptions = (
  projects: ProjectPlanOption[],
  entitlements: OrganisationEntitlementsDto | null,
): ProjectPlanOptionState[] => {
  const projectLimit = entitlements?.plan.limits.projectLimit ?? null;
  if (projectLimit === null) {
    return projects.map((project) => ({ ...project, locked: false, lockReason: null }));
  }

  const orderedProjects = sortProjectsByCreatedAt(projects);
  const unlockedIds = new Set(orderedProjects.slice(0, projectLimit).map((project) => project.id));
  return projects.map((project) => {
    const locked = !unlockedIds.has(project.id);
    return {
      ...project,
      locked,
      lockReason: locked ? projectLockReason : null,
    };
  });
};

const resolveUploadBlockedReason = (
  entitlements: OrganisationEntitlementsDto | null,
  currentProjectLocked: boolean,
  now: Date,
): string | null => {
  if (entitlements === null) {
    return null;
  }

  if (!entitlements.access.currentUserWriteAccess) {
    return 'Only the earliest member can upload runs on the Solo plan.';
  }

  const executionLimit = entitlements.usage.executionLimit;
  if (
    executionLimit !== null &&
    executionLimit > 0 &&
    entitlements.usage.executionCount >= executionLimit
  ) {
    return 'Your plan run limit has been reached. Upgrade to continue uploading.';
  }

  if (entitlements.plan.code === 'trial' && entitlements.trialEndsAt instanceof Date) {
    if (entitlements.trialEndsAt.getTime() <= now.getTime()) {
      return 'Your trial has ended. Upgrade to continue uploading.';
    }
  }

  if (currentProjectLocked) {
    return projectLockReason;
  }

  return null;
};

export const resolvePlanStatus = ({
  entitlements,
  projects,
  selectedProjectId,
  now = new Date(),
}: ResolvePlanStatusInput): ResolvedPlanStatus => {
  const projectOptions = resolveProjectOptions(projects, entitlements);
  const currentProjectLocked =
    selectedProjectId === null
      ? false
      : (projectOptions.find((project) => project.id === selectedProjectId)?.locked ?? false);

  if (entitlements === null) {
    return {
      alerts: [],
      badge: null,
      projectOptions,
      currentProjectLocked,
      uploadBlockedReason: currentProjectLocked ? projectLockReason : null,
    };
  }

  const usageStatus = resolveUsageAlert(entitlements);
  const trialStatus = resolveTrialAlert(entitlements, now);
  const memberStatus = resolveMemberAccessAlert(entitlements, now);
  const alerts = [memberStatus.alert, usageStatus.alert, trialStatus.alert].filter(
    (alert): alert is PlanAlert => alert !== null,
  );
  const badge = memberStatus.badge ?? usageStatus.badge ?? trialStatus.badge ?? null;

  return {
    alerts,
    badge,
    projectOptions,
    currentProjectLocked,
    uploadBlockedReason: resolveUploadBlockedReason(entitlements, currentProjectLocked, now),
  };
};
